import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import React from "react";
import { cn } from "../../utils/cn";
import { useTranslation } from "../../contexts/LanguageContext";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  /** Quando fornecido, exibe o seletor "Linhas por página" dentro da mesma barra. */
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
  className?: string;
  showInfo?: boolean;
  maxVisiblePages?: number;
  hideOnSinglePage?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  className,
  showInfo = true,
  maxVisiblePages = 5,
  hideOnSinglePage = false,
}) => {
  const { t } = useTranslation();

  if (hideOnSinglePage && totalPages <= 1) {
    return null;
  }

  // Calcular range de páginas visíveis
  const getVisiblePages = () => {
    if (totalPages === 0 || totalItems === 0) return [];
    if (totalPages <= 1) return [1];

    const pages: (number | string)[] = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, currentPage + halfVisible);

    if (currentPage <= halfVisible) {
      endPage = Math.min(totalPages, maxVisiblePages);
    }

    if (currentPage > totalPages - halfVisible) {
      startPage = Math.max(1, totalPages - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const navButtonClass = cn(
    "flex items-center justify-center h-7 w-7 rounded-lg transition-colors shrink-0",
    "bg-white border border-gray-200 text-gray-500",
    "hover:border-purple-300 hover:text-purple-700 hover:bg-white",
    "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500",
    "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:text-gray-500 disabled:hover:bg-white",
  );

  return (
    <nav
      aria-label="Navegação de página"
      className={cn(
        "flex items-center justify-between gap-3 w-full flex-wrap sm:flex-nowrap",
        "bg-white! border border-purple-200 rounded-2xl shadow-xs px-3 py-2",
        className,
      )}
    >
      {/* Bloco esquerdo: linhas por página + contagem — uma única unidade de leitura */}
      <div className="flex items-center gap-3 min-w-0">
        {onItemsPerPageChange && (
          <>
            <label
              htmlFor="items-per-page"
              className="flex items-center gap-1.5 text-xs text-gray-500 font-medium shrink-0"
            >
              <span className="hidden sm:inline whitespace-nowrap">
                {t("Linhas por página")}
              </span>
              <div className="relative">
                <select
                  id="items-per-page"
                  value={itemsPerPage}
                  onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                  disabled={totalItems === 0}
                  className="appearance-none bg-white border border-gray-200 rounded-lg pl-2 pr-6 py-1 text-xs font-semibold text-gray-700 hover:border-purple-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {itemsPerPageOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <ChevronDown className="h-3 w-3 absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </label>
            {showInfo && <div className="h-4 w-px bg-gray-200 shrink-0" />}
          </>
        )}

        {showInfo && (
          <p className="text-xs text-gray-500 font-medium whitespace-nowrap truncate">
            <span className="text-gray-800 font-semibold">
              {new Intl.NumberFormat().format(startItem)}–
              {new Intl.NumberFormat().format(endItem)}
            </span>{" "}
            {t("de")}{" "}
            <span className="text-gray-800 font-semibold">
              {new Intl.NumberFormat().format(totalItems)}
            </span>{" "}
            <span className="hidden sm:inline">{t("registros")}</span>
          </p>
        )}
      </div>

      {/* Bloco direito: navegação */}
      <div className="flex items-center gap-1 shrink-0 ml-auto">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1 || totalPages === 0 || totalItems === 0}
          className={navButtonClass}
          aria-label={t("Primeira página")}
        >
          <ChevronsLeft className="h-3.5 w-3.5" />
        </button>

        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || totalPages === 0 || totalItems === 0}
          className={navButtonClass}
          aria-label={t("Página anterior")}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>

        <div className="flex items-center gap-0.5 px-1">
          {visiblePages.map((page, index) => {
            if (page === "...") {
              return (
                <button
                  key={`ellipsis-${index}`}
                  onClick={() => {
                    const direction = index < visiblePages.length / 2 ? -1 : 1;
                    const jump = 5 * direction;
                    onPageChange(
                      Math.max(1, Math.min(totalPages, currentPage + jump)),
                    );
                  }}
                  className="w-5 h-7 flex items-center justify-center text-gray-400 text-xs hover:text-purple-600 transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500 rounded-sm"
                  aria-label={t("Pular 5 páginas")}
                >
                  ···
                </button>
              );
            }

            const pageNumber = page as number;
            const isActive = pageNumber === currentPage;

            return (
              <button
                key={pageNumber}
                onClick={() => onPageChange(pageNumber)}
                disabled={totalPages === 0 || totalItems === 0}
                className={cn(
                  "h-7 min-w-7 px-1 text-xs font-semibold rounded-lg transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500",
                  isActive
                    ? "bg-purple-600 text-white"
                    : "text-gray-600 hover:bg-gray-100 hover:text-purple-700",
                )}
                aria-label={`${t("Ir para página")} ${pageNumber}`}
                aria-current={isActive ? "page" : undefined}
              >
                {pageNumber}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={
            currentPage >= totalPages || totalPages === 0 || totalItems === 0
          }
          className={navButtonClass}
          aria-label={t("Próxima página")}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>

        <button
          onClick={() => onPageChange(totalPages)}
          disabled={
            currentPage >= totalPages || totalPages === 0 || totalItems === 0
          }
          className={navButtonClass}
          aria-label={t("Última página")}
        >
          <ChevronsRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </nav>
  );
};

export default Pagination;
